/**
 * Automatic captaincy.
 *
 * Answers the decisions the innings generator asks for. Used by the CPU
 * opponent, by the calibration harness, and as the fallback when a user
 * abandons a match mid-innings.
 *
 * Deliberately a plain heuristic rather than anything clever: the calibration
 * harness needs a *stable* opponent to measure the ball model against. If this
 * changed behaviour as it "learned", every calibration run would be measuring
 * two moving targets at once.
 */

import {
  simulateInnings,
  MAX_OVERS_PER_BOWLER,
  OVERS_PER_INNINGS,
  bowlerCapacities,
  type Answer,
  type Decision,
  type InningsConfig,
  type InningsResult,
} from './innings.ts';
import { phaseOf } from './baselines.generated.ts';
import { createRng, deriveSeed } from './rng.ts';
import type { Coefficients } from './coefficients.ts';
import type { Player } from './types.ts';

/**
 * Picks the next bowler.
 *
 * Two jobs, in priority order:
 *
 *  1. Never strand the innings. Overs must remain coverable -- if the remaining
 *     bowlers' spare capacity only just covers the remaining overs, anyone who
 *     would break that is skipped. Without this the innings can reach over 19
 *     with only the bowler who bowled over 18 still standing.
 *  2. Save the best for the death. Bowling out a 95-rated quick by over 12 is
 *     the single most common way a human captain loses a T20, and a CPU that
 *     did it would make the game feel easy for the wrong reason.
 */
export function chooseBowler(
  eligible: readonly Player[],
  allBowlers: readonly Player[],
  overIndex: number,
  oversBowled: ReadonlyMap<string, number>,
): Player {
  const oversRemaining = OVERS_PER_INNINGS - overIndex;
  const phase = phaseOf(overIndex);
  const caps = bowlerCapacities(allBowlers);
  const capOf = (p: Player): number => caps.get(p.id) ?? 0;

  /**
   * Can `remaining` overs still be bowled, given these spare capacities and the
   * rule that nobody bowls two overs in a row?
   *
   * Total capacity alone is not enough. Alternation caps any single bowler at
   * ceil(remaining / 2), so four spare overs held by one bowler only cover two
   * of the last four. Ignoring that is what stranded over 20: with exactly five
   * bowling options and 5 x 4 = 20, there is no slack at all, and a greedy pick
   * can leave the last over to the bowler who just bowled the nineteenth.
   */
  const coverable = (caps: readonly number[], remaining: number): boolean => {
    if (remaining <= 0) return true;
    const perBowlerMax = Math.ceil(remaining / 2);
    let usable = 0;
    let total = 0;
    for (const c of caps) {
      total += c;
      usable += Math.min(c, perBowlerMax);
    }
    return total >= remaining && usable >= remaining;
  };

  const stillCoverableAfter = (candidate: Player): boolean => {
    // Capacity must be counted over *every* bowler, not just those eligible
    // right now -- the bowler who just bowled is ineligible this over but
    // available again next over.
    const caps: number[] = [];
    let othersHaveCapacity = false;
    for (const p of allBowlers) {
      if (capOf(p) < 1) continue;
      const used = (oversBowled.get(p.id) ?? 0) + (p.id === candidate.id ? 1 : 0);
      const spare = Math.max(0, capOf(p) - used);
      caps.push(spare);
      if (p.id !== candidate.id && spare > 0) othersHaveCapacity = true;
    }
    const after = oversRemaining - 1;
    // Whoever bowls next over cannot be the candidate, so someone else needs
    // an over left.
    if (after > 0 && !othersHaveCapacity) return false;
    return coverable(caps, after);
  };

  const safe = eligible.filter(stillCoverableAfter);
  const pool = safe.length > 0 ? safe : eligible;

  // Real captains do NOT hoard their best bowlers for the death. Measured over
  // 42,875 real IPL overs, the mean bowler rating by phase is 71.2 powerplay /
  // 68.3 middle / 71.8 death -- a spread of barely three points, because with a
  // four-over quota the top five bowlers cover almost the whole innings anyway.
  //
  // An earlier version reserved the strike bowlers by heavily penalising them
  // outside the death. That produced 72.5 / 55.4 / 73.8: the middle overs were
  // handed to part-timers, inflating middle-overs scoring by 6% while starving
  // the death. The correct behaviour is to spread quality nearly evenly, with
  // only a slight tilt toward the best at the death.
  const strike = [...allBowlers]
    .filter((p) => capOf(p) >= 3)
    .sort((a, b) => b.bowl - a.bowl)
    .slice(0, 2)
    .map((p) => p.id);

  // Pace each bowler through their quota rather than scoring them greedily.
  //
  // Any greedy rule fails here. Ranking purely on rating bowls the best out by
  // over 15 (measured 77 / 62 / 51 across the phases); adding a per-over
  // penalty just moves the collapse earlier. The only way to hold quality flat
  // across an innings is to spread each bowler's overs proportionally through
  // it -- a four-over bowler owes roughly 1.2 overs to the powerplay, 1.8 to
  // the middle and 1.0 to the death.
  //
  // So "furthest behind schedule" is the primary key and rating only breaks
  // ties, which reproduces the real 71 / 68 / 72 profile.
  const expected = (p: Player): number => capOf(p) * (overIndex / OVERS_PER_INNINGS);

  const score = (p: Player): number => {
    const used = oversBowled.get(p.id) ?? 0;
    let s = (expected(p) - used) * 40 + p.bowl * 0.5;
    // The small real-world tilt: best bowlers slightly favoured at the death,
    // slightly held back through the middle.
    if (strike.includes(p.id)) s += phase === 'DEATH' ? 14 : phase === 'MIDDLE' ? -6 : 0;
    return s;
  };

  return [...pool].sort((a, b) => score(b) - score(a))[0];
}

/**
 * Aggression the CPU sets at its strategic timeout.
 *
 * Zero means "normal for this phase", NOT "passive". The phase baselines
 * already encode how real teams bat -- the death-overs rate of 10.06 an over is
 * measured from innings where sides were already going hard. Returning a
 * positive default here stacked extra aggression on top of that and pushed the
 * death rate to 10.85 with sixes 27% above real.
 *
 * The slider exists to express *deviation* from normal, so a captain who leaves
 * it alone should get ordinary cricket.
 */
export function chooseAggression(
  chasing: boolean,
  runsRequired: number,
  ballsRemaining: number,
  scale = 1,
): number {
  if (!chasing || ballsRemaining <= 0) return 0;
  const required = (runsRequired / ballsRemaining) * 6;
  // The thresholds are the shape of a captain's response; `scale` is its size,
  // and is fitted. See `chaseAggressionScale` in coefficients.ts for why the
  // two are separated.
  const base = required > 12 ? 0.8 : required > 10 ? 0.45 : required > 8 ? 0.15 : -0.25;
  return base * scale;
}

/** Answers a decision on the CPU's behalf. */
export function autoAnswer(
  decision: Decision,
  allBowlers: readonly Player[],
  oversBowled: Map<string, number>,
  chase: { runsRequired: number; ballsRemaining: number } | undefined,
  chaseAggressionScale = 1,
): Answer {
  if (decision.type === 'SELECT_BOWLER') {
    const bowler = chooseBowler(decision.eligible, allBowlers, decision.overIndex, oversBowled);
    oversBowled.set(bowler.id, (oversBowled.get(bowler.id) ?? 0) + 1);
    return { type: 'BOWLER', playerId: bowler.id };
  }
  // The CPU takes each break at the end of its window rather than early, so a
  // human opponent keeps the option of calling theirs first.
  if (!decision.forced) return { type: 'DECLINE' };

  return {
    type: 'AGGRESSION',
    value: chooseAggression(
      chase !== undefined,
      chase?.runsRequired ?? 0,
      chase?.ballsRemaining ?? 0,
      chaseAggressionScale,
    ),
  };
}

/**
 * Drives a full match: toss, both innings, result.
 *
 * The toss is not cosmetic. In the source data the winner fields 66% of the
 * time and the chasing side wins 54.7% of matches, so a sim that treated the
 * decision as a coin flip would misprice every match it plays.
 */
export function playMatch(config: {
  readonly seed: string;
  readonly teamA: readonly Player[];
  readonly teamB: readonly Player[];
  readonly coefficients: Coefficients;
}): MatchResult {
  const tossRng = createRng(deriveSeed(config.seed, 'toss'));
  const aWonToss = tossRng.chance(0.5);
  const elected: 'BAT' | 'FIELD' = tossRng.chance(TOSS_FIELD_RATE) ? 'FIELD' : 'BAT';

  // Whoever won the toss gets their choice; the other side takes what is left.
  const aBatsFirst = aWonToss ? elected === 'BAT' : elected === 'FIELD';
  const [first, second] = aBatsFirst ? [config.teamA, config.teamB] : [config.teamB, config.teamA];

  const innings1 = playInnings({
    seed: config.seed,
    inningsIndex: 0,
    battingXI: first,
    bowlingXI: second,
    coefficients: config.coefficients,
  });

  const innings2 = playInnings({
    seed: config.seed,
    inningsIndex: 1,
    battingXI: second,
    bowlingXI: first,
    coefficients: config.coefficients,
    target: innings1.result.runs + 1,
  });

  const chaseWon = innings2.result.runs > innings1.result.runs;
  const tied = innings2.result.runs === innings1.result.runs;

  return {
    innings1: innings1.result,
    innings2: innings2.result,
    battedFirst: aBatsFirst ? 'A' : 'B',
    winner: tied ? 'TIE' : chaseWon === aBatsFirst ? 'B' : 'A',
    chaseWon,
    tied,
  };
}

export interface MatchResult {
  readonly innings1: InningsResult;
  readonly innings2: InningsResult;
  readonly battedFirst: 'A' | 'B';
  readonly winner: 'A' | 'B' | 'TIE';
  readonly chaseWon: boolean;
  readonly tied: boolean;
}

/**
 * Measured share of tosses where the winner elects to field. Real IPL captains
 * chase far more often than they bat.
 */
export const TOSS_FIELD_RATE = 0.664;

/**
 * Drives an innings to completion with no human input.
 *
 * Also records the decisions taken, which is exactly the `decisionLog` the
 * server replays to verify a result.
 */
export function playInnings(config: InningsConfig): {
  result: InningsResult;
  decisions: Answer[];
} {
  const gen = simulateInnings(config);
  const oversBowled = new Map<string, number>();
  const decisions: Answer[] = [];

  let step = gen.next();
  let guard = 0;
  while (!step.done) {
    if (++guard > 500) throw new Error('innings did not terminate -- decision loop is stuck');

    const last = step.value.events.at(-1);
    const answer = autoAnswer(
      step.value.decision,
      config.bowlingXI,
      oversBowled,
      last?.chase,
      config.coefficients.chaseAggressionScale,
    );
    decisions.push(answer);
    step = gen.next(answer);
  }
  return { result: step.value, decisions };
}
